import { Shipment } from "@domain/shipment";
import { format } from "date-fns";
import { TransportMethodIcon } from "@features/shipments/components/shared/TransportMethodIcon";
import { Stack, Box, Typography, useTheme } from "@mui/material";
import { Icon } from "@shared/icons";

interface ShipmentPreviewProps {
    shipment: Shipment;
    quantity?: number;
    onClick?: () => void;
}

interface ShipmentStatus {
    label: string;
    color: string;
}

const ShipmentPreview: React.FC<ShipmentPreviewProps> = ({
    shipment,
    quantity,
    onClick,
}) => {


    const theme = useTheme();

    const getShipmentStatus = (shipment: Shipment): ShipmentStatus => {
        if (shipment.processedDate && shipment.processedDate < new Date()) {
            return {
                label: 'Delivered',
                color: theme.palette.success.main,
            };
        }

        if (shipment.arrivalDate && shipment.arrivalDate < new Date()) {
            return {
                label: 'Processing',
                color: theme.palette.primary.main,
            };
        }

        if (shipment.departureDate && shipment.departureDate < new Date()) {
            return {
                label: 'In transit',
                color: theme.palette.secondary.main,
            };
        }

        return {
            label: 'Planned',
            color: theme.palette.text.secondary,
        };
    };



    const getShipmentDepartureStatus = (shipment: Shipment) => {
        if (shipment.departureDate && shipment.departureDate < new Date()) {
            return 'Departed on ' + format(new Date(shipment.departureDate), 'MMM dd, yyyy');
        }

        if (shipment.arrivalDate && shipment.arrivalDate < new Date()) {
            return 'Arrived on ' + format(new Date(shipment.arrivalDate), 'MMM dd, yyyy');
        }

        return 'Planned';
    };

    return (
        <Stack
            onClick={onClick}
            sx={{
                flexDirection: 'column',
                alignItems: 'flex-start',
                cursor: onClick ? 'pointer' : 'default',
                '&:hover': onClick ? {
                    backgroundColor: 'action.hover',
                    borderRadius: 1,
                } : {},
            }}

            direction="row" width="100%" justifyContent="space-between" alignItems="center" spacing={2}>

            <Stack direction="row" width="100%" justifyContent="space-between" alignItems="center" spacing={2}>

                <Stack direction="row" alignItems="center" spacing={2}>
                    <TransportMethodIcon size={48} transportMethod={shipment.transportMethod} />

                    <Stack direction="column" spacing={0}>

                        {shipment.name && (
                            <Typography variant="subtitle2" color="text.secondary">
                                {shipment.name}
                            </Typography>
                        )}

                        <Typography variant="body2" color="text.primary">
                            {getShipmentDepartureStatus(shipment)}
                        </Typography>

                    </Stack>


                    {quantity && (
                        <Typography variant="body2" color="text.primary">
                            {quantity} items
                        </Typography>
                    )}
                </Stack>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Box sx={{ border: 1, opacity: 0.5, borderColor: getShipmentStatus(shipment).color, borderRadius: 1, p: 1 }}>
                        <Typography variant="subtitle2" color={getShipmentStatus(shipment).color}>
                            {getShipmentStatus(shipment).label}
                        </Typography>
                    </Box>

                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Icon icon="CalendarCheck" />

                        <Typography variant="body2" color="text.primary">
                            {shipment.processedDate ? format(new Date(shipment.processedDate), 'EEE, MMM dd, yyyy') : 'N/A'}
                        </Typography>
                    </Stack>

                </Box>

            </Stack>

        </Stack>
    );
};

export default ShipmentPreview; 